import '@adornis/entity-picker/client/x-entity-picker.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { adminCreateUser } from '../../../api/admin-users.js';
import { Company } from '../../../db/company.js';
import { MatchingWeekRole } from '../../../db/enums/roles.js';
import { isValidSingleEmail } from '../../../shared/email.js';
import { adminRouter } from './admin-routes.js';

type RoleSelection = 'NONE' | 'ADMIN' | 'COMPANY';

export const AdminUserCreate = stateful(() => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<RoleSelection>('NONE');
  const [pickedCompany, setPickedCompany] = useState<{ _id: string; name?: string } | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allValid =
    isValidSingleEmail(email) &&
    firstName.trim() &&
    lastName.trim() &&
    password.trim().length >= 8 &&
    (role !== 'COMPANY' || !!pickedCompany?._id);

  const handleSubmit = async () => {
    setError(null);
    if (!allValid) return;
    setSubmitting(true);
    try {
      const roleArg =
        role === 'ADMIN'
          ? MatchingWeekRole.ADMIN
          : role === 'COMPANY'
            ? MatchingWeekRole.COMPANY
            : '';
      const companyArg = role === 'COMPANY' ? pickedCompany!._id : '';
      const newUserID = await adminCreateUser(
        email.trim(),
        password.trim(),
        firstName.trim(),
        lastName.trim(),
        roleArg,
        companyArg,
      )();
      adminRouter.goTo('userDetail', { params: { userID: newUserID as string } });
    } catch (e: any) {
      setError(e?.message ?? m.admin_user_create_failed());
    } finally {
      setSubmitting(false);
    }
  };

  return html`
    <div class="p-8">
      <div class="flex items-center justify-between mb-6">
        <button class="btn btn-ghost" @click=${() => adminRouter.goTo('users')}>
          <span class="icon-[material-symbols--arrow-back]"></span>
          ${m.admin_user_create_back_to_users()}
        </button>
        <h1 class="text-3xl font-bold">${m.admin_user_create_title()}</h1>
      </div>

      <div class="card bg-base-100 shadow max-w-2xl">
        <div class="card-body gap-4">
          ${error ? html`<div class="alert alert-error">${error}</div>` : nothing}

          <label class="form-control w-full">
            <div class="label"><span class="label-text">${m.base_user_form_email_label()}</span></div>
            <input
              class="input input-bordered w-full"
              type="email"
              placeholder="user@example.com"
              .value=${email}
              @input=${(e: InputEvent) => setEmail((e.target as HTMLInputElement).value)}
            />
          </label>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label class="form-control w-full">
              <div class="label"><span class="label-text">${m.base_user_form_firstName_label()}</span></div>
              <input
                class="input input-bordered w-full"
                .value=${firstName}
                @input=${(e: InputEvent) => setFirstName((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="form-control w-full">
              <div class="label"><span class="label-text">${m.base_user_form_lastName_label()}</span></div>
              <input
                class="input input-bordered w-full"
                .value=${lastName}
                @input=${(e: InputEvent) => setLastName((e.target as HTMLInputElement).value)}
              />
            </label>
          </div>

          <label class="form-control w-full">
            <div class="label"><span class="label-text">${m.admin_user_create_password_label()}</span></div>
            <input
              class="input input-bordered w-full"
              type="password"
              .value=${password}
              @input=${(e: InputEvent) => setPassword((e.target as HTMLInputElement).value)}
            />
          </label>

          <label class="form-control w-full">
            <div class="label"><span class="label-text">${m.admin_user_create_role_label()}</span></div>
            <select
              class="select select-bordered"
              .value=${role}
              @change=${(e: Event) => setRole((e.target as HTMLSelectElement).value as RoleSelection)}
            >
              <option value="NONE">${m.admin_user_create_role_none()}</option>
              <option value="ADMIN">${m.admin_user_create_role_admin()}</option>
              <option value="COMPANY">${m.admin_user_create_role_company()}</option>
            </select>
          </label>

          ${role === 'COMPANY'
            ? html`
                <label class="form-control w-full">
                  <div class="label"><span class="label-text">${m.admin_user_create_company_label()}</span></div>
                  ${EntityPickerInput({
                    class: Company,
                    filter: new AdornisFilter({}),
                    selectionSet: { _id: 1, name: 1 },
                    value: pickedCompany?._id ?? null,
                    placeholder: m.admin_user_create_company_picker_placeholder(),
                    renderString: c => c?.name ?? c?._id ?? '',
                    onEntityPicked: c => setPickedCompany(c ? { _id: c._id!, name: c.name } : null),
                  } as any)}
                </label>
              `
            : nothing}

          <div class="card-actions justify-end mt-2">
            <button class="btn btn-primary" ?disabled=${submitting || !allValid} @click=${handleSubmit}>
              ${submitting ? html`<span class="loading loading-spinner"></span>` : nothing}
              ${m.admin_user_create_title()}
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
});
